import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, Clock, User, MessageSquare } from 'lucide-react';

export function AppointmentDetailsDialog({ open, onOpenChange, appointment, calendarName }) {
  const navigate = useNavigate();
  if (!appointment) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Appointment Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">{appointment.name || 'Unnamed Appointment'}</h3>
              <Badge className={getStatusColor(appointment.status)}>
                {appointment.status}
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-gray-500 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium">{appointment.date}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-gray-500 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Time</p>
                <p className="font-medium">{appointment.time}</p>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <User className="h-4 w-4 text-gray-500 mt-1" />
            <div>
              <p className="text-sm text-gray-500">Calendar</p>
              <p className="font-medium">{calendarName}</p>
            </div>
          </div>

          {appointment.type && (
            <div>
              <p className="text-sm text-gray-500">Type</p>
              <Badge variant="outline">{appointment.type}</Badge>
            </div>
          )}

          {appointment.duration && (
            <div>
              <p className="text-sm text-gray-500">Duration</p>
              <p className="font-medium">{appointment.duration}</p>
            </div>
          )}

          {appointment.notes && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Notes</p>
              <p className="text-sm bg-gray-50 p-3 rounded-md">{appointment.notes}</p>
            </div>
          )}

          {/* Go to Inbox button */}
          {appointment.contactId && (
            <div className="pt-2 border-t">
              <Button
                variant="outline" size="sm"
                className="w-full gap-2 text-indigo-600 border-indigo-200 hover:bg-indigo-50 font-semibold"
                onClick={() => { onOpenChange(false); navigate(`/inbox?contact=${appointment.contactId}`); }}
                data-testid="apt-go-to-inbox"
              >
                <MessageSquare size={14} /> Go to Inbox
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
